import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  Field,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  CompensatingControl,
  ControlEffectiveness,
  EvidenceItem,
  ExceptionGovernance,
  ExceptionRiskLevel,
  ExceptionRoute,
  ExceptionTargetType,
  ExceptionVoteDecision,
  FrameworkRequirement,
  FrameworkVersion,
  ComplianceFramework,
  PolicyExceptionDetail,
  RiskAcceptance,
} from "@odingard/sdk-client";
import { riskAcceptanceIsLive } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, evidenceApi, exceptionsApi, frameworksApi, policyApi } from "../../lib/api";
import {
  GOVERNANCE_PERMISSIONS,
  GovPanel,
  GovernedAction,
  useGovernedMutation,
} from "../../governance";
import {
  AcceptanceStateBadge,
  EffectivenessBadge,
  ExceptionStatusBadge,
  RiskLevelBadge,
  VoteDecisionBadge,
  effectivenessLabel,
  routeModeLabel,
} from "./exceptionLabels";

const RISK_LEVELS: ExceptionRiskLevel[] = ["low", "medium", "high", "critical"];
const EFFECTIVENESS: ControlEffectiveness[] = [
  "unassessed",
  "effective",
  "partially_effective",
  "ineffective",
];
const DECISIONS: ExceptionVoteDecision[] = ["approved", "rejected", "changes_requested"];

interface Member {
  id: string;
  email: string;
  displayName: string;
}

const today = () => new Date().toISOString().slice(0, 10);

// One exception, governed (APP-004 R3 P3).
//
// The screen offers every action the signed in user may hold permission for and
// lets the backend decide: it does not reimplement the approval route, the
// segregation of duties rules or the lifecycle. A refusal is shown with the
// backend's own wording, because "the requester cannot approve their own
// exception" is the answer, not an error to be smoothed over.
export function GovExceptionDetail() {
  const { exceptionId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";

  const record = useAsync<PolicyExceptionDetail>(
    async () => (await policyApi.exception(orgId, exceptionId)).data.exception,
    [orgId, exceptionId],
  );
  const governance = useAsync<ExceptionGovernance>(
    async () => (await exceptionsApi.governance(orgId, exceptionId)).data.governance,
    [orgId, exceptionId],
  );
  const acceptances = useAsync<RiskAcceptance[]>(
    async () => (await exceptionsApi.riskAcceptances(orgId, exceptionId)).data.riskAcceptances,
    [orgId, exceptionId],
  );
  const routes = useAsync<ExceptionRoute[]>(
    async () => (await exceptionsApi.routes(orgId)).data.routes,
    [orgId],
  );
  const members = useAsync<Member[]>(async () => (await api.members(orgId)).members, [orgId]);

  const memberName = (userId: string | null) => {
    if (!userId) return "Unassigned";
    const match = (members.data ?? []).find((m) => m.id === userId);
    return match ? match.displayName || match.email : userId;
  };

  const reloadAll = () => {
    record.reload();
    governance.reload();
    acceptances.reload();
  };

  const exception = record.data;
  const gov = governance.data;
  const revoked = gov?.revokedAt ?? null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{exception?.title ?? "Exception"}</h1>
          <p>
            <Link to="/studio/governance/exceptions">Back to exceptions</Link>
          </p>
          <div className="badge-row">
            {exception ? <ExceptionStatusBadge status={exception.status} /> : null}
            {gov ? <RiskLevelBadge level={gov.riskLevel} /> : null}
            {revoked ? <Badge tone="danger">Revoked</Badge> : null}
            {gov?.escalatedAt ? <Badge tone="warning">Escalated</Badge> : null}
            {gov ? <Badge tone="neutral">Cycle {gov.governanceCycle}</Badge> : null}
          </div>
        </div>
        <div className="badge-row">
          <RenewException orgId={orgId} exceptionId={exceptionId} onDone={reloadAll} />
          <GovernedAction
            permission={GOVERNANCE_PERMISSIONS.exceptionsManage}
            label="Revoke exception"
            title="Revoke this exception"
            description="Withdraws the exception before its end date. The policy applies again from now."
            consequence="Revocation is permanent and recorded with your reason. A renewal is a new request, not an undo."
            confirmLabel="Revoke exception"
            busyLabel="Revoking…"
            destructive
            requireRationale
            rationaleLabel="Reason for revocation"
            onConfirm={(reason) => exceptionsApi.revoke(orgId, exceptionId, reason)}
            onDone={reloadAll}
          />
        </div>
      </div>

      {revoked ? (
        <Card>
          <CardBody>
            <p className="ods-denial" role="status" style={{ margin: 0 }}>
              Revoked {formatDate(revoked)} by {memberName(gov?.revokedBy ?? null)}.{" "}
              {gov?.revocationReason}
            </p>
          </CardBody>
        </Card>
      ) : null}

      <GovPanel
        title="Request"
        state={record}
        loadingLabel="Loading the exception…"
      >
        {(row) => (
          <>
            <dl className="detail-grid">
              <div>
                <dt>Requested by</dt>
                <dd>{memberName(row.requestedBy)}</dd>
              </div>
              <div>
                <dt>Risk owner</dt>
                <dd>{memberName(row.riskOwnerId)}</dd>
              </div>
              <div>
                <dt>Requested window</dt>
                <dd>
                  {formatDate(row.requestedStart)} → {formatDate(row.requestedEnd)}
                </dd>
              </div>
              <div>
                <dt>Approved window</dt>
                <dd>
                  {row.approvedStart
                    ? `${formatDate(row.approvedStart)} → ${formatDate(row.approvedEnd)}`
                    : "Not approved"}
                </dd>
              </div>
              <div>
                <dt>Scope</dt>
                <dd>{row.scope || "Not recorded"}</dd>
              </div>
              <div>
                <dt>Affected systems</dt>
                <dd>
                  {row.affectedSystems.length > 0
                    ? row.affectedSystems.join(", ")
                    : "Not recorded"}
                </dd>
              </div>
            </dl>
            <h3>Business justification</h3>
            <p>{row.businessJustification || "Not recorded"}</p>
            <h3>Risk description</h3>
            <p>{row.riskDescription || "Not recorded"}</p>
            {row.conditions ? (
              <>
                <h3>Conditions</h3>
                <p>{row.conditions}</p>
              </>
            ) : null}
            {row.compensatingControls ? (
              <>
                <h3>Compensating controls (legacy free text)</h3>
                <p>{row.compensatingControls}</p>
                <p className="muted-note">
                  Captured before structured controls existed. It is kept exactly as written and is
                  never rewritten into the structured controls below.
                </p>
              </>
            ) : null}
          </>
        )}
      </GovPanel>

      <GovPanel title="Governance" state={governance} loadingLabel="Loading governance…">
        {(row) => (
          <>
            <dl className="detail-grid">
              <div>
                <dt>Approval route</dt>
                <dd>
                  {row.route ? `${row.route.name} (${routeModeLabel(row.route.mode)})` : "None assigned"}
                </dd>
              </div>
              <div>
                <dt>Risk level</dt>
                <dd>
                  <RiskLevelBadge level={row.riskLevel} />
                </dd>
              </div>
              <div>
                <dt>Next review</dt>
                <dd>{row.reviewAt ? formatDate(row.reviewAt) : "Not scheduled"}</dd>
              </div>
              <div>
                <dt>Governance cycle</dt>
                <dd>{row.governanceCycle}</dd>
              </div>
              <div>
                <dt>Escalated</dt>
                <dd>{row.escalatedAt ? formatDate(row.escalatedAt) : "No"}</dd>
              </div>
            </dl>
            <AssignGovernance
              orgId={orgId}
              exceptionId={exceptionId}
              governance={row}
              routes={routes.data ?? []}
              onDone={reloadAll}
            />
          </>
        )}
      </GovPanel>

      <GovPanel
        title="What this exception covers"
        state={governance}
        loadingLabel="Loading targets…"
        action={
          <AddTarget orgId={orgId} exceptionId={exceptionId} onDone={governance.reload} />
        }
        isEmpty={(row) => row.targets.length === 0}
        emptyTitle="No targets"
        emptyDescription="Add the policies, sections or requirements this exception departs from."
        emptyAction={<AddTarget orgId={orgId} exceptionId={exceptionId} onDone={governance.reload} />}
      >
        {(row) => (
          <DataTable
            caption="Exception targets"
            columns={[
              { key: "label", header: "Target", render: (t) => <strong>{t.label}</strong> },
              { key: "type", header: "Type", render: (t) => t.targetType.replace(/_/g, " ") },
              {
                key: "id",
                header: "Identifier",
                render: (t) => (
                  <code>
                    {t.targetId}
                    {t.sectionKey ? ` § ${t.sectionKey}` : ""}
                  </code>
                ),
              },
              {
                key: "actions",
                header: "",
                render: (t) => (
                  <GovernedAction
                    permission={GOVERNANCE_PERMISSIONS.exceptionsManage}
                    label="Remove"
                    title="Remove this target"
                    description="The exception will no longer be recorded against this policy or requirement."
                    consequence="Coverage reporting changes immediately: the target goes back to being governed by the policy in full."
                    confirmLabel="Remove target"
                    busyLabel="Removing…"
                    destructive
                    onConfirm={() => exceptionsApi.removeTarget(orgId, exceptionId, t.id)}
                    onDone={governance.reload}
                  />
                ),
              },
            ]}
            rows={row.targets}
          />
        )}
      </GovPanel>

      <GovPanel
        title="Compensating controls"
        state={governance}
        loadingLabel="Loading compensating controls…"
        action={<AddControl orgId={orgId} exceptionId={exceptionId} members={members.data ?? []} onDone={governance.reload} />}
        isEmpty={(row) => row.controls.length === 0}
        emptyTitle="No structured compensating controls"
        emptyDescription="Add what reduces the risk while this exception is in force, and who owns it."
        emptyAction={
          <AddControl orgId={orgId} exceptionId={exceptionId} members={members.data ?? []} onDone={governance.reload} />
        }
      >
        {(row) => (
          <div className="stack">
            {row.controls.map((control) => (
              <ControlCard
                key={control.id}
                orgId={orgId}
                control={control}
                memberName={memberName}
                onDone={governance.reload}
              />
            ))}
          </div>
        )}
      </GovPanel>

      <GovPanel title="Approval route" state={governance} loadingLabel="Loading approvals…">
        {(row) => (
          <>
            {row.route ? (
              <p className="muted-note">
                {routeModeLabel(row.route.mode)} route “{row.route.name}” ·{" "}
                {row.route.requiredApprovals} of {row.route.steps.length} approvals required
                {row.route.requiresRiskAcceptance
                  ? " · a live risk acceptance is required before approval"
                  : ""}
                .{" "}
                {row.route.mode === "sequential"
                  ? "Approvers act in order; acting early is refused."
                  : "Approvers act in any order until the required set is satisfied."}
              </p>
            ) : (
              <p className="muted-note">
                No route is assigned, so this exception follows the organization default.
              </p>
            )}
            <div className="badge-row">
              {row.approvalsSatisfied ? (
                <Badge tone="success">Approvals satisfied</Badge>
              ) : (
                <Badge tone="info">
                  Waiting on {row.pendingApprovers.length || "an"} approver
                  {row.pendingApprovers.length === 1 ? "" : "s"}
                </Badge>
              )}
              {row.pendingApprovers.map((approver) => (
                <Badge key={approver} tone="neutral">
                  {memberName(approver)}
                </Badge>
              ))}
            </div>
            {row.routeApprovals.length > 0 ? (
              <DataTable
                caption="Decisions recorded this cycle"
                columns={[
                  { key: "step", header: "Step", render: (v) => v.stepPosition + 1 },
                  { key: "approver", header: "Approver", render: (v) => memberName(v.approverId) },
                  {
                    key: "decision",
                    header: "Decision",
                    render: (v) => <VoteDecisionBadge decision={v.decision} />,
                  },
                  {
                    key: "rationale",
                    header: "Rationale",
                    render: (v) => v.rationale || "None recorded",
                  },
                  { key: "at", header: "Decided", render: (v) => formatDate(v.decidedAt) },
                ]}
                rows={row.routeApprovals}
              />
            ) : (
              <p className="muted-note">No decision has been recorded in this cycle yet.</p>
            )}
            <div className="badge-row">
              <RecordDecision orgId={orgId} exceptionId={exceptionId} onDone={reloadAll} />
            </div>
          </>
        )}
      </GovPanel>

      <GovPanel
        title="Residual risk"
        state={acceptances}
        loadingLabel="Loading risk acceptances…"
        action={
          <AcceptRisk orgId={orgId} exceptionId={exceptionId} onDone={reloadAll} />
        }
        isEmpty={(rows) => rows.length === 0}
        emptyTitle="No risk acceptance"
        emptyDescription="Nobody has accepted the residual risk of this exception."
        emptyAction={<AcceptRisk orgId={orgId} exceptionId={exceptionId} onDone={reloadAll} />}
      >
        {(rows) => (
          <div className="stack">
            {rows.map((acceptance) => (
              <AcceptanceCard
                key={acceptance.id}
                orgId={orgId}
                acceptance={acceptance}
                currentCycle={gov?.governanceCycle ?? acceptance.governanceCycle}
                memberName={memberName}
                onDone={reloadAll}
              />
            ))}
          </div>
        )}
      </GovPanel>

      <GovPanel title="History" state={record} loadingLabel="Loading history…">
        {(row) => (
          <>
            <p className="muted-note">
              Decisions are only ever added. Nothing here is edited or removed, including decisions
              from earlier governance cycles.
            </p>
            {row.decisions.length === 0 ? (
              <p className="muted-note">No decisions recorded.</p>
            ) : (
              <DataTable
                caption="Exception history"
                columns={[
                  { key: "decision", header: "Event", render: (d) => d.decision },
                  { key: "actor", header: "By", render: (d) => memberName(d.approverId) },
                  {
                    key: "rationale",
                    header: "Rationale",
                    render: (d) => d.rationale || "None recorded",
                  },
                  {
                    key: "window",
                    header: "Window",
                    render: (d) =>
                      d.approvedStart
                        ? `${formatDate(d.approvedStart)} → ${formatDate(d.approvedEnd)}`
                        : "Not set",
                  },
                  { key: "at", header: "When", render: (d) => formatDate(d.decidedAt) },
                ]}
                rows={row.decisions}
              />
            )}
          </>
        )}
      </GovPanel>
    </>
  );
}

// ----- governance assignment -------------------------------------------------

function AssignGovernance({
  orgId,
  exceptionId,
  governance,
  routes,
  onDone,
}: {
  orgId: string;
  exceptionId: string;
  governance: ExceptionGovernance;
  routes: ExceptionRoute[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [routeId, setRouteId] = useState(governance.routeId ?? "");
  const [riskLevel, setRiskLevel] = useState(governance.riskLevel ?? "");
  const [reviewAt, setReviewAt] = useState(governance.reviewAt ?? "");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.exceptionsManage);

  const submit = async () => {
    const ok = await mutation.run(() =>
      exceptionsApi.assignGovernance(orgId, exceptionId, {
        routeId: routeId || null,
        riskLevel: (riskLevel || null) as ExceptionRiskLevel | null,
        reviewAt: reviewAt || null,
      }),
    );
    if (!ok) return;
    setOpen(false);
    onDone();
  };

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Set route, risk and review
      </Button>
      <Dialog
        open={open}
        title="Governance for this exception"
        description="Chooses who approves it, how risky it is, and when it must be looked at again."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={mutation.busy} onClick={() => void submit()}>
              {mutation.busy ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Approval route" help="Leave unset to follow the organization default route.">
          {(id) => (
            <Select id={id} value={routeId} onChange={(e) => setRouteId(e.target.value)}>
              <option value="">Organization default</option>
              {routes.map((route) => (
                <option key={route.id} value={route.id}>
                  {route.name} ({routeModeLabel(route.mode)})
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Risk level">
          {(id) => (
            <Select id={id} value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
              <option value="">Not classified</option>
              {RISK_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Next review" help="High and critical exceptions are escalated when a review comes due.">
          {(id) => (
            <TextInput
              id={id}
              type="date"
              value={reviewAt}
              onChange={(e) => setReviewAt(e.target.value)}
            />
          )}
        </Field>
      </Dialog>
    </>
  );
}

// ----- targets ---------------------------------------------------------------

function AddTarget({
  orgId,
  exceptionId,
  onDone,
}: {
  orgId: string;
  exceptionId: string;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [targetType, setTargetType] = useState<ExceptionTargetType>("requirement");
  const [versionId, setVersionId] = useState("");
  const [requirementId, setRequirementId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [sectionKey, setSectionKey] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.exceptionsManage);

  const versions = useAsync<{ version: FrameworkVersion; framework: ComplianceFramework }[]>(
    async () => {
      if (!open || targetType !== "requirement") return [];
      const frameworks = (await frameworksApi.frameworks(orgId, { scope: "tenant" })).data.frameworks;
      return (
        await Promise.all(
          frameworks.map(async (framework) =>
            (await frameworksApi.versions(orgId, framework.id)).data.versions.map((version) => ({
              version,
              framework,
            })),
          ),
        )
      ).flat();
    },
    [orgId, open, targetType],
  );
  const version = versionId || versions.data?.[0]?.version.id || "";
  const requirements = useAsync<FrameworkRequirement[]>(
    async () =>
      open && targetType === "requirement" && version
        ? (await frameworksApi.requirements(orgId, version)).data.requirements
        : [],
    [orgId, version, open, targetType],
  );
  const requirement = requirementId || requirements.data?.[0]?.id || "";

  const policies = useAsync(
    async () => (open && targetType !== "requirement" ? (await policyApi.policies(orgId)).data.policies : []),
    [orgId, open, targetType],
  );
  const policyList = policies.data ?? [];
  const policyTarget =
    targetId ||
    (targetType === "policy"
      ? policyList[0]?.id
      : policyList[0]?.currentVersionId) ||
    "";

  const target = targetType === "requirement" ? requirement : policyTarget;

  const submit = async () => {
    const ok = await mutation.run(() =>
      exceptionsApi.addTarget(orgId, exceptionId, {
        targetType,
        targetId: target,
        sectionKey: targetType === "policy_version_section" ? sectionKey.trim() : null,
      }),
    );
    if (!ok) return;
    setOpen(false);
    setTargetId("");
    setSectionKey("");
    onDone();
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
        Add target
      </Button>
      <Dialog
        open={open}
        title="Add what this exception covers"
        description="An exception applies to named policies, policy sections or framework requirements. It never applies to everything."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!target || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Adding…" : "Add target"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Target type">
          {(id) => (
            <Select
              id={id}
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as ExceptionTargetType)}
            >
              <option value="requirement">Requirement</option>
              <option value="policy">Policy</option>
              <option value="policy_version_section">Policy version section</option>
            </Select>
          )}
        </Field>

        {targetType === "requirement" ? (
          <>
            <Field label="Framework version">
              {(id) => (
                <Select id={id} value={version} onChange={(e) => setVersionId(e.target.value)}>
                  {(versions.data ?? []).map(({ version: v, framework }) => (
                    <option key={v.id} value={v.id}>
                      {framework.name} {v.versionLabel}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Requirement">
              {(id) => (
                <Select
                  id={id}
                  value={requirement}
                  onChange={(e) => setRequirementId(e.target.value)}
                >
                  {(requirements.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.requirementKey}: {r.title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </>
        ) : (
          <>
            <Field label={targetType === "policy" ? "Policy" : "Policy version"}>
              {(id) => (
                <Select id={id} value={policyTarget} onChange={(e) => setTargetId(e.target.value)}>
                  {policyList.map((policy) => (
                    <option
                      key={policy.id}
                      value={targetType === "policy" ? policy.id : (policy.currentVersionId ?? "")}
                    >
                      {policy.title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {targetType === "policy_version_section" ? (
              <Field label="Section key" help="The section of that version the exception departs from.">
                {(id) => (
                  <TextInput
                    id={id}
                    value={sectionKey}
                    onChange={(e) => setSectionKey(e.target.value)}
                  />
                )}
              </Field>
            ) : null}
          </>
        )}
      </Dialog>
    </>
  );
}

// ----- compensating controls -------------------------------------------------

function AddControl({
  orgId,
  exceptionId,
  members,
  onDone,
}: {
  orgId: string;
  exceptionId: string;
  members: Member[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.exceptionsManage);
  const owner = ownerId || members[0]?.id || "";

  const submit = async () => {
    const ok = await mutation.run(() =>
      exceptionsApi.addControl(orgId, exceptionId, {
        title: title.trim(),
        description: description.trim(),
        ownerId: owner,
      }),
    );
    if (!ok) return;
    setOpen(false);
    setTitle("");
    setDescription("");
    onDone();
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
        Add control
      </Button>
      <Dialog
        open={open}
        title="Add a compensating control"
        description="What reduces the risk while the exception is in force, and who is answerable for it."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!title.trim() || !owner || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Adding…" : "Add control"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Title">
          {(id) => <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <Field label="Description">
          {(id) => (
            <TextArea
              id={id}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Owner"
          help="The owner cannot assess their own control while it can still affect the approval decision."
        >
          {(id) => (
            <Select id={id} value={owner} onChange={(e) => setOwnerId(e.target.value)}>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.displayName || member.email}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </Dialog>
    </>
  );
}

function ControlCard({
  orgId,
  control,
  memberName,
  onDone,
}: {
  orgId: string;
  control: CompensatingControl;
  memberName: (userId: string | null) => string;
  onDone: () => void;
}) {
  const evidence = useAsync(
    async () => (await exceptionsApi.controlEvidence(orgId, control.id)).data.evidence,
    [orgId, control.id],
  );
  const assessments = useAsync(
    async () => (await exceptionsApi.assessments(orgId, control.id)).data.assessments,
    [orgId, control.id],
  );
  const linked = evidence.data ?? [];

  return (
    <Card>
      <CardHeader
        title={control.title}
        action={
          <div className="badge-row">
            <LinkControlEvidence orgId={orgId} controlId={control.id} onDone={evidence.reload} />
            <AssessControl
              orgId={orgId}
              controlId={control.id}
              linkedEvidenceIds={linked.map((l) => l.evidenceItemId)}
              onDone={() => {
                assessments.reload();
                onDone();
              }}
            />
          </div>
        }
      />
      <CardBody>
        <div className="badge-row">
          <EffectivenessBadge effectiveness={control.effectiveness} />
          <span className="muted-note">Owner {memberName(control.ownerId)}</span>
          {control.assessedAt ? (
            <span className="muted-note">
              Assessed {formatDate(control.assessedAt)} by {memberName(control.assessedBy)}
            </span>
          ) : (
            <span className="muted-note">Never assessed</span>
          )}
        </div>
        {control.description ? <p>{control.description}</p> : null}

        <h4>Linked evidence</h4>
        {linked.length === 0 ? (
          <p className="muted-note">
            No evidence is linked. A positive effectiveness assessment must cite linked evidence.
          </p>
        ) : (
          <ul>
            {linked.map((link) => (
              <li key={link.evidenceItemId}>
                <Link to={`/studio/governance/evidence/items/${link.evidenceItemId}`}>
                  {link.evidenceItemId}
                </Link>{" "}
                <Badge tone="neutral">{link.linkRole}</Badge>{" "}
                <span className="muted-note">linked {formatDate(link.linkedAt)}</span>
              </li>
            ))}
          </ul>
        )}

        <h4>Assessment history</h4>
        {(assessments.data ?? []).length === 0 ? (
          <p className="muted-note">No assessment has been recorded.</p>
        ) : (
          <DataTable
            caption={`Assessment history for ${control.title}`}
            columns={[
              {
                key: "effectiveness",
                header: "Effectiveness",
                render: (a) => <EffectivenessBadge effectiveness={a.effectiveness} />,
              },
              { key: "actor", header: "Assessed by", render: (a) => memberName(a.actorId) },
              {
                key: "rationale",
                header: "Rationale",
                render: (a) => a.rationale || "None recorded",
              },
              {
                key: "evidence",
                header: "Evidence cited",
                render: (a) =>
                  a.evidenceItemIds.length > 0 ? a.evidenceItemIds.join(", ") : "None cited",
              },
              { key: "at", header: "When", render: (a) => formatDate(a.assessedAt) },
            ]}
            rows={assessments.data ?? []}
          />
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Evidence is linked from the evidence side, so the link carries the same
 * lifecycle and role rules as every other evidence link. Only approved items
 * are offered here: unapproved evidence is not proof of a working control.
 */
function LinkControlEvidence({
  orgId,
  controlId,
  onDone,
}: {
  orgId: string;
  controlId: string;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [itemId, setItemId] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.evidenceManage);
  const items = useAsync<EvidenceItem[]>(
    async () => (open ? (await evidenceApi.items(orgId, { status: "approved" })).data.items : []),
    [orgId, open],
  );
  const selected = itemId || items.data?.[0]?.id || "";

  const submit = async () => {
    const ok = await mutation.run(() =>
      evidenceApi.addLink(orgId, selected, {
        targetType: "compensating_control",
        targetId: controlId,
        linkRole: "supports",
      }),
    );
    if (!ok) return;
    setOpen(false);
    onDone();
  };

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Link evidence
      </Button>
      <Dialog
        open={open}
        title="Link approved evidence to this control"
        description="Only approved evidence items are offered. The link is created on the evidence record itself."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!selected || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Linking…" : "Link evidence"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Approved evidence">
          {(id) => (
            <Select id={id} value={selected} onChange={(e) => setItemId(e.target.value)}>
              {(items.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </Dialog>
    </>
  );
}

function AssessControl({
  orgId,
  controlId,
  linkedEvidenceIds,
  onDone,
}: {
  orgId: string;
  controlId: string;
  linkedEvidenceIds: string[];
  onDone: () => void;
}) {
  const [effectiveness, setEffectiveness] = useState<ControlEffectiveness>("effective");
  const [cited, setCited] = useState<string[]>([]);

  const toggle = (id: string) =>
    setCited((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.exceptionsManage}
      label="Assess"
      title="Assess this control"
      description="Records whether the control is actually working, and on what basis."
      consequence="Every assessment is kept: this one is added to the history rather than replacing the last one."
      confirmLabel="Record assessment"
      busyLabel="Recording…"
      requireRationale
      rationaleLabel="Basis for this assessment"
      onConfirm={(rationale) =>
        exceptionsApi.assessControl(orgId, controlId, {
          effectiveness,
          rationale,
          evidenceItemIds: cited,
        })
      }
      onDone={() => {
        setCited([]);
        onDone();
      }}
    >
      <Field label="Effectiveness">
        {(id) => (
          <Select
            id={id}
            value={effectiveness}
            onChange={(e) => setEffectiveness(e.target.value as ControlEffectiveness)}
          >
            {EFFECTIVENESS.map((value) => (
              <option key={value} value={value}>
                {effectivenessLabel(value)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <fieldset>
        <legend>Evidence cited</legend>
        {linkedEvidenceIds.length === 0 ? (
          <p className="muted-note">
            Nothing is linked to this control yet. Effective and partially effective assessments must
            cite linked evidence.
          </p>
        ) : (
          linkedEvidenceIds.map((id) => (
            <label key={id} className="ods-row" style={{ gap: 8 }}>
              <input type="checkbox" checked={cited.includes(id)} onChange={() => toggle(id)} />{" "}
              {id}
            </label>
          ))
        )}
      </fieldset>
    </GovernedAction>
  );
}

// ----- approvals -------------------------------------------------------------

/**
 * The decision itself is a governed action; who may cast it, in what
 * order, and whether a risk acceptance must exist first are all the backend's
 * to answer. A refusal appears inside this dialog with the backend's wording.
 */
function RecordDecision({
  orgId,
  exceptionId,
  onDone,
}: {
  orgId: string;
  exceptionId: string;
  onDone: () => void;
}) {
  const [decision, setDecision] = useState<ExceptionVoteDecision>("approved");
  const [approvedStart, setApprovedStart] = useState("");
  const [approvedEnd, setApprovedEnd] = useState("");
  const [conditions, setConditions] = useState("");

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.exceptionApprove}
      label="Record decision"
      title="Record your decision on this exception"
      description="Your decision is recorded against the route step you hold. The exception is only decided once the route is satisfied."
      consequence="Decisions cannot be edited or withdrawn. Requesting changes starts a new governance cycle, which invalidates the approvals and risk acceptance recorded so far."
      confirmLabel="Record decision"
      busyLabel="Recording…"
      triggerVariant="primary"
      requireRationale
      onConfirm={(rationale) =>
        exceptionsApi.vote(orgId, exceptionId, {
          decision,
          rationale,
          approvedStart: approvedStart || null,
          approvedEnd: approvedEnd || null,
          conditions: conditions.trim(),
        })
      }
      onDone={onDone}
    >
      <Field label="Decision">
        {(id) => (
          <Select
            id={id}
            value={decision}
            onChange={(e) => setDecision(e.target.value as ExceptionVoteDecision)}
          >
            {DECISIONS.map((value) => (
              <option key={value} value={value}>
                {value.replace(/_/g, " ")}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {decision === "approved" ? (
        <>
          <Field label="Approved from" help="Leave empty to approve the window as requested.">
            {(id) => (
              <TextInput
                id={id}
                type="date"
                value={approvedStart}
                onChange={(e) => setApprovedStart(e.target.value)}
              />
            )}
          </Field>
          <Field label="Approved until" help="An approval may narrow the requested window, never widen it.">
            {(id) => (
              <TextInput
                id={id}
                type="date"
                value={approvedEnd}
                onChange={(e) => setApprovedEnd(e.target.value)}
              />
            )}
          </Field>
          <Field label="Conditions">
            {(id) => (
              <TextArea
                id={id}
                rows={2}
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
              />
            )}
          </Field>
        </>
      ) : null}
    </GovernedAction>
  );
}

// ----- risk acceptance -------------------------------------------------------

function AcceptRisk({
  orgId,
  exceptionId,
  onDone,
}: {
  orgId: string;
  exceptionId: string;
  onDone: () => void;
}) {
  const [residualRisk, setResidualRisk] = useState<ExceptionRiskLevel>("medium");
  const [conditions, setConditions] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.riskAccept}
      label="Accept residual risk"
      title="Accept the residual risk"
      description="Records who is answerable for what remains once the compensating controls are taken into account."
      consequence="Accepting risk is a personal, recorded decision. It cannot be edited. It can only be revoked, which is also recorded."
      confirmLabel="Accept risk"
      busyLabel="Recording…"
      triggerVariant="primary"
      requireRationale
      rationaleLabel="Why this residual risk is acceptable"
      onConfirm={(rationale) =>
        exceptionsApi.acceptRisk(orgId, exceptionId, {
          residualRisk,
          rationale,
          conditions: conditions.trim(),
          expiresAt: expiresAt || null,
        })
      }
      onDone={onDone}
    >
      <Field label="Residual risk">
        {(id) => (
          <Select
            id={id}
            value={residualRisk}
            onChange={(e) => setResidualRisk(e.target.value as ExceptionRiskLevel)}
          >
            {RISK_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Conditions">
        {(id) => (
          <TextArea
            id={id}
            rows={2}
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
          />
        )}
      </Field>
      <Field label="Expires" help="An expired acceptance stops covering the exception on that date.">
        {(id) => (
          <TextInput
            id={id}
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        )}
      </Field>
    </GovernedAction>
  );
}

function AcceptanceCard({
  orgId,
  acceptance,
  currentCycle,
  memberName,
  onDone,
}: {
  orgId: string;
  acceptance: RiskAcceptance;
  currentCycle: number;
  memberName: (userId: string | null) => string;
  onDone: () => void;
}) {
  const live = riskAcceptanceIsLive(acceptance, today());
  const state = acceptance.revokedAt
    ? "revoked"
    : live
      ? acceptance.governanceCycle === currentCycle
        ? "active"
        : "none"
      : "expired";

  return (
    <Card>
      <CardHeader
        title={`Residual risk accepted by ${memberName(acceptance.acceptedBy)}`}
        action={
          acceptance.revokedAt === null ? (
            <GovernedAction
              permission={GOVERNANCE_PERMISSIONS.exceptionsManage}
              label="Revoke acceptance"
              title="Revoke this risk acceptance"
              description="Withdraws the acceptance. The exception is no longer covered by it."
              consequence="Revocation is permanent and recorded. If the route requires a live acceptance, approval is blocked until a new one is recorded."
              confirmLabel="Revoke acceptance"
              busyLabel="Revoking…"
              destructive
              requireRationale
              rationaleLabel="Reason for revocation"
              onConfirm={(reason) => exceptionsApi.revokeAcceptance(orgId, acceptance.id, reason)}
              onDone={onDone}
            />
          ) : null
        }
      />
      <CardBody>
        <div className="badge-row">
          <AcceptanceStateBadge state={state} />
          <RiskLevelBadge level={acceptance.residualRisk} />
          <Badge tone="neutral">Cycle {acceptance.governanceCycle}</Badge>
          {acceptance.governanceCycle !== currentCycle ? (
            <Badge tone="warning">Earlier governance cycle</Badge>
          ) : null}
        </div>
        <dl className="detail-grid">
          <div>
            <dt>Accepted</dt>
            <dd>{formatDate(acceptance.acceptedAt)}</dd>
          </div>
          <div>
            <dt>Expires</dt>
            <dd>{acceptance.expiresAt ? formatDate(acceptance.expiresAt) : "No expiry"}</dd>
          </div>
          {acceptance.revokedAt ? (
            <div>
              <dt>Revoked</dt>
              <dd>
                {formatDate(acceptance.revokedAt)} by {memberName(acceptance.revokedBy)}
              </dd>
            </div>
          ) : null}
        </dl>
        <p>{acceptance.rationale}</p>
        {acceptance.conditions ? (
          <p className="muted-note">Conditions: {acceptance.conditions}</p>
        ) : null}
        {acceptance.revocationReason ? (
          <p className="ods-denial" role="status">
            No longer valid for this exception. {acceptance.revocationReason}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

// ----- renewal ---------------------------------------------------------------

function RenewException({
  orgId,
  exceptionId,
  onDone,
}: {
  orgId: string;
  exceptionId: string;
  onDone: () => void;
}) {
  const [requestedStart, setRequestedStart] = useState(today());
  const [requestedEnd, setRequestedEnd] = useState("");
  const [reviewAt, setReviewAt] = useState("");

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.exceptionsManage}
      label="Renew"
      title="Renew this exception"
      description="Raises a successor request for a new window, carrying the targets and controls across."
      consequence="The renewal is a new request that must be approved again. Control effectiveness is reset, and approvals and risk acceptance do not carry over."
      confirmLabel="Request renewal"
      busyLabel="Requesting…"
      onConfirm={() =>
        exceptionsApi.renew(orgId, exceptionId, {
          requestedStart,
          requestedEnd,
          reviewAt: reviewAt || null,
        })
      }
      onDone={onDone}
    >
      <Field label="Requested from">
        {(id) => (
          <TextInput
            id={id}
            type="date"
            value={requestedStart}
            onChange={(e) => setRequestedStart(e.target.value)}
          />
        )}
      </Field>
      <Field label="Requested until">
        {(id) => (
          <TextInput
            id={id}
            type="date"
            value={requestedEnd}
            onChange={(e) => setRequestedEnd(e.target.value)}
          />
        )}
      </Field>
      <Field label="Next review">
        {(id) => (
          <TextInput
            id={id}
            type="date"
            value={reviewAt}
            onChange={(e) => setReviewAt(e.target.value)}
          />
        )}
      </Field>
    </GovernedAction>
  );
}
